
import { useState, useCallback } from 'react';
import { cartService } from '../../services/cartService';
import { GlobalErrorHandler } from '../../errors';
import type { Result } from '../../errors/types';

type UpdateType = 'increase' | 'decrease' | 'remove' | 'input' | null;

export const useCartItemActions = (itemKey: string, currentQty: number) => {
    const [updatingType, setUpdatingType] = useState<UpdateType>(null);

    const changeQty = useCallback(async (type: UpdateType, customQty?: number): Promise<Result<void> | undefined> => {
        setUpdatingType(type);

        let newQty: number;
        let result: Result<void> | undefined;

        if (type === 'increase') newQty = currentQty + 1;
        else if (type === 'decrease') newQty = currentQty - 1;
        else if (type === 'remove') newQty = 0;
        else if (type === 'input' && customQty !== undefined) newQty = customQty;
        else newQty = currentQty;

        try {
            console.log(`🎯 [useCartItemActions] changeQty called:`, {
                type,
                customQty,
                newQty,
                itemKey
            });

            if (newQty === 0) {
                console.log('[useCartItemActions] Calling removeLineItem');
                result = await cartService.removeLineItem(itemKey);
            } else {
                console.log('[useCartItemActions] Calling updateLineItem');
                result = await cartService.updateLineItem(itemKey, newQty);
            }

            console.log('[useCartItemActions] Service result:', {
                ok: result.ok,
                hasError: !result.ok,
                errorCode: !result.ok ? result.error?.code : undefined,
                errorContext: !result.ok ? result.error?.context : undefined
            });

            // 检查 Service 返回结果，如果失败则调用错误处理模块
            if (!result.ok && result.error) {
                console.warn('🚨 [useCartItemActions] Calling GlobalErrorHandler with:', result.error);
                GlobalErrorHandler.handle(result.error, {
                    scope: 'cartDrawer',
                    source: 'qtyStepper',
                    userInitiated: true,
                    correlationId: itemKey
                });
                console.log('[useCartItemActions] GlobalErrorHandler called');
            } else {
                console.log('✅ [useCartItemActions] Operation succeeded, no error handling needed');
            }
        } finally {
            setUpdatingType(null);
        }
        return result;
    }, [itemKey, currentQty, updatingType]);

    return {
        increase: () => changeQty('increase'),
        decrease: () => changeQty('decrease'),
        remove: () => changeQty('remove'),
        updateInput: (qty: number) => changeQty('input', qty),
        updatingType
    };
};
